import { usePage } from "@/components/page/PageContext";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/stores/NotificationsContext";
import { Bell, Loader2 } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const NotificationsPage: React.FC = () => {
  const { setTitles } = usePage();
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    error,
    markAllAsRead,
    markAsRead,
    clearNotifications,
  } = useNotifications();

  // Set page title
  useEffect(() => {
    setTitles(["Notifications"]);
  }, [setTitles]);

  // Handle notification click
  const handleNotificationClick = async (notification: any) => {
    // Check if it's a scheduled play notification and has a play_id
    if (
      notification.play_id &&
      notification.title === "Scheduled Play Starting Soon"
    ) {
      // Navigate to scheduled plays with the play ID as query parameter
      await markAsRead(notification.id);
      navigate(`/scheduled-plays?playId=${notification.play_id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12 h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">Error: {error}</p>
        <p className="mt-2">Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="mx-12 mt-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl">Notifications</h1>
        <div className="space-x-2">
          {notifications.some((n) => !n.is_read) && (
            <Button variant="outline" onClick={() => markAllAsRead()} size="sm">
              Mark all as read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="outline"
              onClick={() => clearNotifications()}
              size="sm"
            >
              Clear all
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Bell className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No notifications
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You'll receive notifications here when scheduled plays are about to
            start.
          </p>
        </div>
      ) : (
        <div className="rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map((notification) => (
              <li
                key={notification.id}
                className={`p-4 ${!notification.is_read ? "bg-blue-50 dark:bg-blue-900/20" : ""} ${notification.play_id ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800" : ""}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <Bell
                      className={`h-5 w-5 ${!notification.is_read ? "text-blue-500" : "text-gray-400"}`}
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(notification.timestamp)
                          .toLocaleString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            day: "2-digit",
                            month: "2-digit",
                          })
                          .replace(",", "")}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {notification.message}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
